import styles from "./map-component.module.css";

import { REACT_APP_GOOGLE_API_KEY } from "../../state/configuration";
import { useEffect, useState } from "react";
import { BaseWizardProps } from "../wizard/wizard-step-base";
import {
  GoAButton,
  GoAFormItem,
  GoAIcon,
  GoASpacer,
  GoATextArea,
} from "@abgov/react-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../state/store";
import { WizardButtonGroup } from "../wizard/wizard-button-group-component";
import {
  APIProvider,
  Map,
  MapControl,
  ControlPosition,
} from "@vis.gl/react-google-maps";
import { updateCollision } from "../../state/slices/collisionSlice";
import { useIsDesktop } from "../../utils/isDesktop";
import { Collision } from "../../models/collision";
import _ from "lodash";

interface LocationReviewProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected
) => TSelected = useSelector;

export const LocationReviewComponent = ({
  navigationConfiguration,
}: LocationReviewProps) => {
  const maxAdditionalLocationDetailsLenth = 150;
  const apiKey = REACT_APP_GOOGLE_API_KEY;
  const isDesktop = useIsDesktop();

  const dispatch = useAppDispatch();

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const [nextPage, setNextPage] = useState("");
  const [additionalLocationDetails, setAdditionalLocationDetails] = useState(
    stateCollision.collision.location?.additionalLocationDetails ?? ""
  );
  const [lat, setLat] = useState(
    stateCollision.collision.location?.latitude ?? 0
  );
  const [lng, setLng] = useState(
    stateCollision.collision.location?.longitude! ?? 0
  );
  const [zoom, setZoom] = useState(
    stateCollision.collision.location?.zoom ?? 15
  );

  const [locationString, setLocationString] = useState(
    stateCollision.collision.location?.locationString ?? ""
  );

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined
    );

    setNextPage(pageConfig?.default ?? "error");
  }, []);

  const validate = (): boolean => {
    if (additionalLocationDetails.length > maxAdditionalLocationDetailsLenth) {
      return false;
    }

    return true;
  };

  const save = () => {
    let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

    updatedCollision.location = {
      ...stateCollision.collision.location!,
      additionalLocationDetails: additionalLocationDetails,
    };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <GoASpacer vSpacing="xs" />
      <h3>Your collision location details</h3>
      <GoASpacer vSpacing="m" />
      <h2 className="location-screen">{locationString}</h2>
      <GoASpacer vSpacing="xl" />
      <GoAFormItem label="Additional location details" requirement="optional">
        <GoATextArea
          name="additionalLocationDetails"
          onChange={() => {}}
          maxCount={maxAdditionalLocationDetailsLenth}
          countBy="character"
          value={additionalLocationDetails}
          onKeyPress={(item, value) => {
            setAdditionalLocationDetails(value);
          }}
          width="100%"
          maxWidth="350px"
        ></GoATextArea>
      </GoAFormItem>
      <GoASpacer vSpacing="l" />
      <div>
        <ReviewMapComponent
          apiKey={apiKey}
          lat={lat}
          lng={lng}
          zoom={zoom}
        ></ReviewMapComponent>
      </div>

      <WizardButtonGroup
        nextPage={nextPage}
        save={save}
        validate={validate}
        goToReview={navigationConfiguration.goToReview}
        showViewSummaryButton={false}
        navigate={navigationConfiguration.navigate}
        customButton={
          <GoAButton
            type="tertiary"
            onClick={() => {
              navigationConfiguration.navigate("#map");
            }}
          >
            Edit Location
          </GoAButton>
        }
      ></WizardButtonGroup>
      <GoASpacer vSpacing="l" />
    </div>
  );
};

export interface ReviewMapComponentProps {
  apiKey: string;
  lat: number;
  lng: number;
  zoom: number;
  className?: string;
  onLoaded?: () => void;
}
export const ReviewMapComponent = ({
  apiKey,
  lat,
  lng,
  zoom,
  className,
  onLoaded,
}: ReviewMapComponentProps) => {
  return (
    <APIProvider apiKey={apiKey} onLoad={() => {}}>
      <Map
        className={className ? className : styles.map}
        styles={[{ stylers: [{ saturation: "-100" }] }]}
        mapTypeId="roadmap"
        defaultZoom={zoom}
        center={{ lat: lat, lng: lng }}
        disableDefaultUI={true}
        gestureHandling={"none"}
        clickableIcons={false}
        onClick={(e) => {}}
        onIdle={() => {
          if (onLoaded) {
            onLoaded();
          }
        }}
      >
        <MapControl position={ControlPosition.CENTER}>
          <GoAIcon type="location" size="large" theme="filled"></GoAIcon>
        </MapControl>
      </Map>
    </APIProvider>
  );
};
